import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import Table from "../../components/Table";
import { ModalAlert, NewSupplieForm, MoveStockModal } from "./Modals";
import api from "../../api";

const stockLevel = (row) => {
    if (Number(row.stock) === 0) return "red";
    if (Number(row.stock) <= Number(row.minStock)) return "yellow";
    return "green";
};

const stockColors = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
};

const StockBadge = (row) => (
    <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${stockColors[stockLevel(row)]}`}
    >
        {row.stock}
    </span>
);

export const supplyTypeLabels = {
    single_use: "Único uso",
    reusable: "Reutilizable",
    resterilizable: "Resterilizable",
    other: "Otro",
};

const columns = [
    {
        name: "category",
        displayText: "Nombre del insumo",
        truncate: true,
    },
    {
        name: "supplyType",
        displayText: "Tipo",
        render: (row) => supplyTypeLabels[row.supplyType] || row.supplyType,
    },
    { name: "minStock", displayText: "Stock mínimo" },
    { name: "stock", displayText: "Stock Total", render: StockBadge },
    { name: "orStock", displayText: "Stock Quirófano" },
    { name: "expirationDate", displayText: "Fecha de vencimiento" },
    { name: "unitCost", displayText: "Costo unitario" },
    { name: "brand", displayText: "Marca" },
];

const userRole = () => JSON.parse(localStorage.getItem("BDOuserData"))?.role;

export function SuppliesPage() {
    const role = userRole();
    const canEditCatalog = role === 4;
    const canMoveStock = role === 4 || role === 6;
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [supplies, setSupplies] = useState(null);
    const [lowStock, setLowStock] = useState([]);
    const [expiring, setExpiring] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [filter, setFilter] = useState([]);
    const stockAlertRef = useRef(null);
    const expirationAlertRef = useRef(null);
    const newSupplieRef = useRef(null);
    const moveStockRef = useRef(null);
    const [selectedSupply, setSelectedSupply] = useState(null);

    const handleOpenMoveStock = (row) => {
        setSelectedSupply(row);
        moveStockRef.current?.showModal();
    };

    const showAlerts = (lowStock, expiring) => {
        if (lowStock.length > 0) {
            stockAlertRef.current.showModal();
        }
        if (expiring.length > 0) {
            expirationAlertRef.current.showModal();
        }
    };

    useEffect(() => {
        setError("");
        api.supplies
            .getMulti("alphabetic", null, null, null, filter)
            .then((response) => {
                const { data, lowStock, expiring } = response;
                showAlerts(lowStock, expiring);
                setSupplies(data);
                setLowStock(lowStock);
                setExpiring(expiring);
                setLoading(false);
            })
            .catch((e) => {
                setError(e.detail || "No se pudo cargar el stock");
                setLoading(false);
            });
    }, [filter]);

    useEffect(() => {
        api.categories.getMulti().then((response) => {
            setCategories(response);
        });
        api.brands.getMulti().then((response) => {
            setBrands(response);
        });
    }, []);

    return (
        <div className="bg-gray-100 h-screen">
            <Sidebar
                currentScreen={"Insumos"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
            />
            <div className="md:pl-64 flex flex-col h-screen flex-1">
                <>
                    <Header setSidebarOpen={setSidebarOpen} />
                    <main className="flex-1 space-y-6 bg-gray-100 py-8 px-6 lg:px-8">
                        <LocalHeader
                            dialogRef={newSupplieRef}
                            canEditCatalog={canEditCatalog}
                            canMoveStock={canMoveStock}
                        />
                        <StockFilter setFilter={setFilter} brands={brands} />
                        {loading ? (
                            <div className="flex h-full items-center justify-center ">
                                <div className="w-16 h-16 border-b-2 border-blue-900 rounded-full animate-spin"></div>
                            </div>
                        ) : error ? (
                            <p className="text-red-600">{error}</p>
                        ) : (
                            <div className="">
                                <Table
                                    module="supplies"
                                    actiontext={canEditCatalog ? "Editar" : undefined}
                                    columns={columns}
                                    rows={supplies}
                                    openModal={canMoveStock ? handleOpenMoveStock : undefined}
                                    openModalText="Mover stock"
                                />
                            </div>
                        )}
                    </main>
                </>
            </div>
            <dialog
                ref={stockAlertRef}
                id="stock-alert"
                closedby="any"
                className={`rounded-l ${
                    expiring.length > 0 && "-top-10 -left-10"
                }`}
            >
                <ModalAlert
                    dialogRef={stockAlertRef}
                    title={"Alerta de Stock"}
                    subTitle={"Te quedan pocos insumos de:"}
                    list={lowStock}
                    type={"stock"}
                />
            </dialog>
            <dialog
                ref={expirationAlertRef}
                id="expiration-alert"
                closedby="any"
                className="rounded-lg"
            >
                <ModalAlert
                    dialogRef={expirationAlertRef}
                    title={"Alerta de Vencimiento"}
                    subTitle={"Insumos que estan prontos a vencer:"}
                    list={expiring}
                    type={"expiration"}
                />
            </dialog>
            <dialog
                ref={newSupplieRef}
                id="new-supplie"
                closedby="any"
                className="rounded-lg overflow-visible"
            >
                <NewSupplieForm
                    dialogRef={newSupplieRef}
                    categories={categories}
                    brands={brands}
                    setCategories={setCategories}
                    setBrands={setBrands}
                    setSupplies={setSupplies}
                />
            </dialog>
            <dialog
                ref={moveStockRef}
                id="move-stock"
                closedby="any"
                className="rounded-lg overflow-visible"
            >
                <MoveStockModal
                    dialogRef={moveStockRef}
                    supply={selectedSupply}
                    setSupplies={setSupplies}
                    restrictToOrActions={role === 6}
                />
            </dialog>
        </div>
    );
}

const LocalHeader = (props) => {
    const openDialog = () => {
        props.dialogRef.current?.showModal();
    };
    return (
        <div className="flex justify-between items-center">
            <h2 className="text-gray-600 text-4xl font-bold">Stock</h2>
            <div className="flex items-center gap-3 mr-2 sm:mr-8 lg:mr-16">
                <Link
                    to="/supplies/movements"
                    className="text-sm font-semibold text-primary hover:underline"
                >
                    Ver movimientos
                </Link>
                {props.canEditCatalog && (
                    <button
                        className="flex items-center gap-2 px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                        onClick={openDialog}
                    >
                        <PlusIcon className="h-6 w-6" /> <span>Nuevo insumo</span>
                    </button>
                )}
            </div>
        </div>
    );
};

const StockFilter = (props) => {
    const [type, setType] = useState("text");
    const [localFilter, setLocalFilter] = useState([]);

    const handleChange = (field, value) => {
        setLocalFilter((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        props.setFilter(localFilter);
    };

    const clearFilter = () => {
        setLocalFilter({
            search: "",
            category: "",
            expiration_date: "",
            stock: "",
            brand: "",
            supply_type: "",
        });
        props.setFilter({
            search: "",
            category: "",
            expiration_date: "",
            stock: "",
            brand: "",
            supply_type: "",
        });
    };
    return (
        <form
            className="flex flex-col sm:flex-row gap-3"
            onSubmit={handleSubmit}
        >
            <div className="">
                <label htmlFor="table-search" className="sr-only">
                    Search
                </label>
                <div className="relative">
                    <div className="absolute w-full inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon
                            className="h-5 w-5 text-blue-600"
                            aria-hidden="true"
                        />
                    </div>
                    <input
                        id="table-search"
                        name="table-search"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                        type="search"
                        value={localFilter.search}
                        onChange={(e) => handleChange("search", e.target.value)}
                        placeholder="Buscar..."
                    />
                </div>
            </div>
            <input
                type={type}
                placeholder="Fecha de vencimiento"
                onFocus={() => setType("date")}
                onBlur={(e) => {
                    if (!e.target.value) setType("text");
                }}
                name="expiration_date"
                id="expiration_date"
                value={localFilter.expiration_date}
                onChange={(e) =>
                    handleChange("expiration_date", e.target.value)
                }
                className="sm:w-44 w-full border border-gray-300 rounded-md bg-white placeholder-black focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
            />
            <input
                type="number"
                name="stock"
                id="stock"
                placeholder="Stock"
                value={localFilter.stock}
                onChange={(e) => handleChange("stock", e.target.value)}
                className="sm:w-32 w-full border border-gray-300 rounded-md bg-white placeholder-black focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
            />
            <select
                name="brand"
                id="brand"
                onChange={(e) => handleChange("brand", e.target.value)}
                className="sm:w-32 w-full border border-gray-300 rounded-md bg-white placeholder-black focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                value={localFilter.brand}
                defaultValue={""}
            >
                <option value="" hidden>
                    Marca
                </option>
                {props.brands.map((brand) => (
                    <option value={brand.id}>{brand.name}</option>
                ))}
            </select>
            <select
                name="supply_type"
                id="supply_type"
                onChange={(e) => handleChange("supply_type", e.target.value)}
                className="sm:w-40 w-full border border-gray-300 rounded-md bg-white placeholder-black focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                value={localFilter.supply_type}
                defaultValue={""}
            >
                <option value="" hidden>
                    Tipo de insumo
                </option>
                {Object.entries(supplyTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
            <div className="flex gap-1">
                <button
                    type="submit"
                    className="px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                >
                    Aplicar
                </button>
                <button
                    type="button"
                    onClick={clearFilter}
                    className="px-4 py-2 border shadow-sm text-sm font-semibold rounded-md text-primary bg-white border-primary hover:bg-gray-200"
                >
                    Limpiar
                </button>
            </div>
        </form>
    );
};
